import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import {
  catchError,
  combineLatest,
  concat,
  exhaustMap,
  filter,
  from,
  map,
  of,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { AuthActions } from '../state/auth/auth.actions';
import { UserActions } from '../state/user/user.actions';
import { HttpErrorResponse } from '@angular/common/http';
import { UserSettingsActions } from '../state/user-settings/user-settings.actions';
import { LocalStorageKeys } from '../../consts/shared.const';
import {
  ConnectivityTimerService,
  SessionStorageService,
  errMessage,
} from '@ui';
import { UserSettingsModels } from '@share-models';
import { ChatOrganizationContextActions } from '@share-chat';
import { OrganizationActions } from '../state/organization/organization.actions';
import { PermissionsActions } from '../state/permissions/premissions.actions';
import { ConnectivityActions } from '../state/connectivity/connectivity.actions';
import { routerSegments } from '../../consts/routes';
import { userFeature } from '../state/user/user.feature';
import { userSettingsFeature } from '../state/user-settings/user-settings.feature';
import { organizationFeature } from '../state/organization/organization.feature';
import { permissionsFeature } from '../state/permissions/permissions.feature';
import { TranslateService } from '@ngx-translate/core';
import { SharedActions } from '../state/shared/shared.actions';

export const startLoadRequiredData = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    sessionStorageService = inject(SessionStorageService),
  ) => {
    return actions$.pipe(
      ofType(AuthActions.startLoadRequiredData),
      switchMap(() => {
        const interfaceLanguage = sessionStorageService.getItem(
          LocalStorageKeys.LANGUAGE,
        );
        if (interfaceLanguage) {
          sessionStorageService.removeItem(LocalStorageKeys.LANGUAGE);
        }

        const fanOutActions = [
          UserActions.getUserProfile(),
          UserSettingsActions.getMe({
            language: interfaceLanguage ?? UserSettingsModels.LANGUAGE.ENGLISH,
          }),
          OrganizationActions.getActiveOrganization(),
          OrganizationActions.getMyOrganizations(),
          PermissionsActions.getUserPermissions(),
        ];

        // App bootstrap (see initApp in app.config.ts) waits on
        // requiredDataLoadedSuccess before route guards can run, so it must
        // only fire once every permission-relevant slice — profile, user
        // settings, active organization, permissions — has settled
        // (success or fail), not just as soon as the fastest one resolves.
        //
        // This relies on each feature's `loaded` flag starting `false` here,
        // which holds today because startLoadRequiredData only ever fires
        // once per page load (once from initApp's bootstrap, once from the
        // one-time OAuth code exchange) and every logout path hard-navigates
        // (see AuthService.logout), tearing down the whole store. If a
        // future change ever re-dispatches startLoadRequiredData within the
        // same session without a full reload, these flags must be reset to
        // false first, or a stale `true` from the prior load will let this
        // fire immediately and reintroduce the guard/navigation race this
        // code exists to prevent.
        const requiredDataLoaded$ = combineLatest([
          store.select(userFeature.selectLoaded),
          store.select(userSettingsFeature.selectLoaded),
          store.select(organizationFeature.selectLoaded),
          store.select(permissionsFeature.selectLoaded),
        ]).pipe(
          filter(
            ([
              userLoaded,
              userSettingsLoaded,
              organizationLoaded,
              permissionsLoaded,
            ]) =>
              userLoaded &&
              userSettingsLoaded &&
              organizationLoaded &&
              permissionsLoaded,
          ),
          take(1),
          map(() => AuthActions.requiredDataLoadedSuccess()),
        );

        return concat(of(...fanOutActions), requiredDataLoaded$);
      }),
    );
  },
  { functional: true },
);

/** The token request is independent from the post-token organization load. */
export const switchOrganization = createEffect(
  (
    actions$ = inject(Actions),
    authService = inject(AuthService),
    translate = inject(TranslateService, { optional: true }),
  ) =>
    actions$.pipe(
      ofType(AuthActions.switchOrganizationRequested),
      exhaustMap(({ organizationId }) =>
        from(authService.switchTenant(organizationId)).pipe(
          map(() => AuthActions.organizationSwitched({ organizationId })),
          // Surface the real server failure; a static message makes a 4xx from
          // POST /okta/auth/switch-organization undiagnosable from the UI.
          catchError((error: unknown) =>
            of(
              error instanceof HttpErrorResponse
                ? AuthActions.organizationSwitchFailed({
                    organizationId,
                    message: errMessage(error),
                    status: error.status,
                  })
                : AuthActions.organizationSwitchFailed({
                    organizationId,
                    message: String(
                      translate?.instant(
                        'CHAT_AUTH.ORGANIZATION_SWITCH_FAILED',
                      ) ?? 'Unable to switch organization. Please try again.',
                    ),
                  }),
            ),
          ),
        ),
      ),
    ),
  { functional: true },
);

/** A failed token request leaves the current token and organization intact. */
export const showOrganizationSwitchFailure = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(AuthActions.organizationSwitchFailed),
      map(({ message }) => SharedActions.showMessage({ error: message })),
    ),
  { functional: true },
);

/**
 * Once the bearer belongs to the target organization, discard only scoped
 * data. The organization list is user-scoped and remains stable throughout.
 */
export const reloadOrganizationDataAfterOrganizationSwitch = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(
        AuthActions.organizationSwitched,
        AuthActions.retryOrganizationDataLoad,
      ),
      switchMap(({ organizationId }) =>
        of(
          ChatOrganizationContextActions.switchStarted({ organizationId }),
          ConnectivityActions.resetToInitialState(),
          PermissionsActions.resetToInitialState(),
          OrganizationActions.loadSwitchedActiveOrganization({
            organizationId,
          }),
          PermissionsActions.getUserPermissions(),
        ),
      ),
    ),
  { functional: true },
);

/**
 * Successful post-token hydration is reached only for the correlated target
 * and only after permissions have settled. Active-organization failure has a
 * separate terminal action and can never pass this barrier.
 */
export const completeOrganizationDataAfterSwitch = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(OrganizationActions.setSwitchedActiveOrganization),
      switchMap(({ organizationId }) =>
        combineLatest([
          store.select(organizationFeature.selectActiveOrganization),
          store.select(organizationFeature.selectSwitchTargetOrganizationId),
          store.select(permissionsFeature.selectLoaded),
        ]).pipe(
          filter(
            ([activeOrganization, targetOrganizationId, permissionsLoaded]) =>
              activeOrganization?._id === organizationId &&
              targetOrganizationId === organizationId &&
              permissionsLoaded,
          ),
          take(1),
          map(() =>
            AuthActions.organizationDataLoadedSuccess({ organizationId }),
          ),
        ),
      ),
    ),
  { functional: true },
);

/** Surface a post-token load error through the chat context Retry state. */
export const failChatContextWhenOrganizationDataFails = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(AuthActions.organizationDataLoadFailed),
      map(({ organizationId, message }) =>
        ChatOrganizationContextActions.failed({
          organizationId,
          synapseSpaceId: null,
          message,
        }),
      ),
    ),
  { functional: true },
);

/**
 * Dialogs, room URL and inactivity timing are organization-specific. Matrix identity/client are
 * intentionally retained; share-chat switches only its organization context.
 */
export const protectOrganizationSwitch = createEffect(
  (
    actions$ = inject(Actions),
    router = inject(Router),
    dialog = inject(MatDialog),
    connectivityTimer = inject(ConnectivityTimerService),
  ) =>
    actions$.pipe(
      ofType(AuthActions.organizationSwitched),
      tap(() => {
        connectivityTimer.stopTimer();
        dialog.closeAll();
        void router.navigateByUrl(routerSegments.home);
      }),
    ),
  { functional: true, dispatch: false },
);

/** Start a fresh inactivity timer only after the new organization has loaded. */
export const restartConnectivityAfterOrganizationSwitch = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(AuthActions.organizationDataLoadedSuccess),
      withLatestFrom(
        store.select(organizationFeature.selectActiveOrganization),
      ),
      filter(
        ([{ organizationId }, organization]) =>
          organization?._id === organizationId,
      ),
      map(() => ConnectivityActions.registerUserActivity()),
    ),
  { functional: true },
);

/** Release host switch controls after the independent chat-context flow settles. */
export const completeOrganizationSwitchControls = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(
        ChatOrganizationContextActions.ready,
        ChatOrganizationContextActions.failed,
        ChatOrganizationContextActions.unavailable,
      ),
      map(({ organizationId }) =>
        OrganizationActions.completeOrganizationSwitch({ organizationId }),
      ),
    ),
  { functional: true },
);

export const socialLoginByCode = createEffect(
  (actions$ = inject(Actions), api = inject(AuthService)) => {
    return actions$.pipe(
      ofType(AuthActions.socialLoginByCode),
      switchMap(({ code }) =>
        api.socialLoginByCode(code).pipe(
          map((authResponse) =>
            UserActions.setStoreSession({
              data: {
                idToken: authResponse.id_token,
                accessToken: authResponse.access_token,
                expiredAt: authResponse.expires_in,
              },
            }),
          ),
          catchError((err: HttpErrorResponse) => {
            const msg = errMessage(err);
            return of(AuthActions.logout({ msg }));
          }),
        ),
      ),
    );
  },
  { functional: true },
);
